import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { stripHtml } from "string-strip-html";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import "react-toastify/dist/ReactToastify.css";
import { toTitleCase, useTranslatedText, useTranslation } from "../contexts/TranslationContext";

const LINK_STYLE = "color: #2277da; text-decoration: none;";
const PLAIN_URL_REGEX = /\b(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

const ensureLinksOpenInNewTab = (html: string): string =>
  html.replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
    let newAttrs = attrs;
    if (!/\btarget\s*=/.test(attrs)) newAttrs += ' target="_blank"';
    if (!/\brel\s*=/.test(attrs)) newAttrs += ' rel="noopener noreferrer"';
    if (!/\bstyle\s*=/.test(attrs)) newAttrs += ` style="${LINK_STYLE}"`;
    return `<a${newAttrs}>`;
  });

const linkifyPlainUrls = (text: string): string =>
  text
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (part.startsWith("<")) return part;
      return part.replace(PLAIN_URL_REGEX, (url) => {
        const href = url.startsWith("www.") ? `https://${url}` : url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="${LINK_STYLE}">${url}</a>`;
      });
    })
    .join("");

/** Preserva enlaces HTML, convierte URLs planas en links y abre todo en nueva pestaña. */
export const processToastMessage = (message: string, stripOtherHtml = true): string => {
  if (!message) return "";

  let processed = stripOtherHtml
    ? stripHtml(message, { ignoreTags: ["a", "br"] }).result
    : message;

  processed = ensureLinksOpenInNewTab(processed);
  processed = linkifyPlainUrls(processed);

  return processed;
};

// Helper function para reducir código duplicado
const createToastConfig = (borderColor: string, iconName: string, onClose?: () => void) => ({
  position: "top-right" as const,
  autoClose: 10000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  progress: undefined,
  onClose,
  style: {
    borderTop: `5px solid ${borderColor}`,
    fontSize: "12px",
    color: "#424242",
  },
  icon: (
    <div
      style={{
        background: borderColor,
        color: "white",
        borderRadius: "100vh",
        width: "30px",
        height: "30px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "22px",
      }}
    >
      <SlIcon
        style={{ color: "white" }}
        name={iconName}
      />
    </div>
  ),
});

export const showToastErrorSoporte = () => {
  const messageWithLink =
    'Por favor intentá de nuevo en unos minutos. De persistir el problema avisanos por acá <a href="https://utdt.edu/Soporte" target="_blank" rel="noopener noreferrer" style="color: #2277da; text-decoration: none;">utdt.edu/Soporte</a>';

  toast(
    <CustomToast msg={processToastMessage(messageWithLink, false)} title="Surgió un error" />,
    createToastConfig("#E31616", "x")
  );
};

// Detecta el mensaje genérico de error de red que produce axios (`error.message === "Network Error"`)
// y se propaga desde los thunks vía `rejectWithValue(error.message)`. En esos casos preferimos
// mostrar el toast genérico de soporte en lugar de "¡Ups! Network Error".
const isNetworkErrorMessage = (message: string): boolean => {
  const messageLower = (message || "").trim().toLowerCase();
  return messageLower === "network error" || messageLower.includes("network");
};

interface TProps {
  msg: string;
  title: string;
}

const translateHtmlPreservingTags = async (
  html: string,
  translate: (text: string) => Promise<string>,
): Promise<string> => {
  const parts = html.split(/(<[^>]+>)/g);
  let insideAnchor = false;
  const result: string[] = [];

  for (const part of parts) {
    const isTag = part.startsWith("<");
    if (isTag) {
      if (/^<a\b/i.test(part)) insideAnchor = true;
      if (/^<\/a\b/i.test(part)) insideAnchor = false;
      result.push(part);
      continue;
    }

    const trimmed = part.trim();
    if (!trimmed || insideAnchor) {
      result.push(part);
      continue;
    }

    try {
      const translated = await translate(trimmed);
      result.push(part.replace(trimmed, translated));
    } catch {
      result.push(part);
    }
  }

  return result.join("");
};

const COURSE_REFERENCE_REGEX = /^(.+?)\s*(?:-\s*)?(S\d+)(\s*\([^)]*\))?$/i;

const translateTextPart = async (
  text: string,
  translate: (text: string) => Promise<string>,
): Promise<string> => {
  const trimmed = text.trim();
  if (!trimmed || !/\p{L}/u.test(trimmed)) return text;

  const translated = await translate(trimmed);
  return text.replace(trimmed, translated);
};

/**
 * Traduce las referencias de cursos por separado para evitar híbridos como
 * `Sociedads S1`. El nombre se traduce como título y la sección se conserva.
 */
const translateToastText = async (
  text: string,
  translate: (text: string) => Promise<string>,
): Promise<string> => {
  const parts: string[] = [];
  const quotedTextRegex = /(["“])([^"”]+)(["”])/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = quotedTextRegex.exec(text)) !== null) {
    const index = match.index;
    parts.push(await translateTextPart(text.slice(lastIndex, index), translate));

    const [, openingQuote, quotedText, closingQuote] = match;
    const course = quotedText.trim().match(COURSE_REFERENCE_REGEX);
    if (course) {
      const [, subjectName, section, code = ""] = course;
      const translatedSubject = toTitleCase(await translate(subjectName.trim()));
      parts.push(`${openingQuote}${translatedSubject} - ${section.toUpperCase()}${code}${closingQuote}`);
    } else {
      parts.push(`${openingQuote}${await translateTextPart(quotedText, translate)}${closingQuote}`);
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex === 0) return translate(text);

  parts.push(await translateTextPart(text.slice(lastIndex), translate));
  return parts.join("");
};

const CustomToast = ({ msg, title }: TProps) => {
  const { lang, translate } = useTranslation();
  const hasHtml = /<[^>]*>/.test(msg);
  const shouldTranslateHtml = hasHtml && lang === "en";
  const shouldTranslatePlainText = !hasHtml && lang === "en";
  const translatedTitle = useTranslatedText(title);
  const [translatedMsg, setTranslatedMsg] = useState(msg);
  const [translatedHtml, setTranslatedHtml] = useState(msg);

  useEffect(() => {
    if (!shouldTranslatePlainText) {
      setTranslatedMsg(msg);
      return;
    }

    let cancelled = false;
    translateToastText(msg, translate)
      .then((result) => {
        if (!cancelled) setTranslatedMsg(result);
      })
      .catch(() => {
        if (!cancelled) setTranslatedMsg(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldTranslatePlainText, msg, translate]);

  useEffect(() => {
    if (!shouldTranslateHtml) {
      setTranslatedHtml(msg);
      return;
    }

    let cancelled = false;
    translateHtmlPreservingTags(msg, translate)
      .then((result) => {
        if (!cancelled) setTranslatedHtml(result);
      })
      .catch(() => {
        if (!cancelled) setTranslatedHtml(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldTranslateHtml, msg, translate]);

  return (
    <div>
      <h4 style={{ fontWeight: "bold", fontSize: "14px" }}>{translatedTitle}</h4>
      {hasHtml ? (
        <div
          style={{ fontSize: "12px" }}
          dangerouslySetInnerHTML={{ __html: translatedHtml }}
        />
      ) : (
        <p style={{ fontSize: "12px" }}>{translatedMsg}</p>
      )}
    </div>
  );
};

export const useToastError = () => {
  const showError = (message: string, onClose?: () => void, title?: string, shouldStripHtml: boolean = true) => {
    if (isNetworkErrorMessage(message)) {
      showToastErrorSoporte();
      return;
    }
    const msg = processToastMessage(message || "", shouldStripHtml);
    toast(<CustomToast msg={msg} title={title || "¡Ups!"} />, createToastConfig("#E31616", "x", onClose));
  };
  return showError;
};

export const useToastInfo = () => {
  const showInfo = (message: string, onClose?: () => void) => {
    const msg = processToastMessage(message || "");
    toast(<CustomToast msg={msg} title="Info" />, createToastConfig("#00C4B3", "info-lg", onClose));
  };
  return showInfo;
};

export const useToastSuccess = () => {
  const showSuccess = (message: string, title?: string, onClose?: () => void) => {
    const msg = processToastMessage(message || "");
    toast(<CustomToast msg={msg} title={title || "¡Operación exitosa!"} />, createToastConfig("#00C4B3", "check", onClose));
  };
  return showSuccess;
};

export const useToastWarning = () => {
  const showWarning = (message: string, onClose?: () => void) => {
    const msg = processToastMessage(message || "");
    toast(<CustomToast msg={msg} title={"¡Atención!"} />, createToastConfig("#F5A800", "exclamation-lg", onClose));
  };
  return showWarning;
};

export const showToastError = (message: string, onClose?: () => void) => {
  if (isNetworkErrorMessage(message)) {
    showToastErrorSoporte();
    return;
  }
  const msg = processToastMessage(message || "");
  toast(<CustomToast msg={msg} title={"¡Ups!"} />, createToastConfig("#E31616", "x", onClose));
};

export const showToastWarning = (message: string, onClose?: () => void) => {
  const msg = processToastMessage(message || "");
  toast(<CustomToast msg={msg} title={"¡Atención!"} />, createToastConfig("#F5A800", "exclamation-lg", onClose));
};
